import axios from 'axios';

// Используем относительный путь, nginx проксирует на backend
const API_BASE_URL = '/api';

// Создаем экземпляр axios с базовыми настройками
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  // 15.09.2026: раньше таймаута не было вовсе — зависший запрос висел вечно,
  // спиннер крутился без ошибки и без реакции. 180с выбраны не «на глаз»: это
  // ровно proxy_read_timeout у nginx для admin.moonraid.online/api/. То есть
  // клиент не может оборвать ничего, что nginx и так бы дождался — это
  // страховка от мёртвого сокета, а не ограничение на долгие отчёты.
  timeout: 180000,
});

// Интерцептор для добавления токена авторизации
// 🔒 SECURITY: Убран DEV bypass с известным токеном
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Без токена запрос пойдёт без авторизации - бэкенд вернёт 401
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Если не авторизован, перенаправляем на страницу логина
      localStorage.removeItem('adminToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API методы для админки
export const adminAPI = {
  // Авторизация
  login: (credentials) => api.post('/admin/login', credentials),
  checkAuth: () => api.get('/admin/check-auth'),
  logout: () => api.post('/admin/logout'),
  
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard'),
  
  // Пользователи
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserById: (id) => api.get(`/admin/users/${id}`),
  getUserReferralChain: (id) => api.get(`/admin/users/${id}/referral-chain`),
  getUserMoneyFlow: (id) => api.get(`/admin/users/${id}/money-flow`),
  getUserReferralsDetailed: (userId, params) => api.get(`/admin/users/${userId}/referrals-detailed`, { params }),
  getUserReferralStats: (userId) => api.get(`/admin/users/${userId}/referral-stats`),
  getUserReferralTree: (userId, params) => api.get(`/admin/users/${userId}/referral-tree`, { params }),
  banUser: (userId, ban) => api.post(`/admin/users/${userId}/ban`, { ban }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  updateUserBalance: (userId, data) => api.post(`/admin/users/${userId}/balance`, data),
  updateUserUsdtBalance: (userId, data) => api.post(`/admin/users/${userId}/usdt-balance`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),

  // Блокировка пользователей (расширенная)
  blockUser: (userId, data) => api.post(`/admin/users/${userId}/block`, data),
  unblockUser: (userId) => api.post(`/admin/users/${userId}/unblock`),
  warnUser: (userId, data) => api.post(`/admin/users/${userId}/warn`, data),
  getUserWarnings: (userId) => api.get(`/admin/users/${userId}/warnings`),
  getBlockStatus: (userId) => api.get(`/admin/users/${userId}/block-status`),

  // Транзакции
  getTransactions: (params) => api.get('/admin/transactions', { params }),
  getTransactionById: (id) => api.get(`/admin/transactions/${id}`),
  
  // Настройки
  getSettings: () => api.get('/admin/settings'),
  saveSettings: (settings) => api.post('/admin/settings', settings),
  updateSettings: (settings) => api.post('/admin/settings', settings),
  updateSetting: (category, key, value) => 
    api.post('/admin/settings/update', { category, key, value_text: value }),
  
  // Статистика
  getStats: (params) => api.get('/admin/stats', { params }),
  
  // Стейкинги
  getStakings: (params) => api.get('/admin/stakings', { params }),
  getUserStakings: (userId) => api.get(`/admin/users/${userId}/stakings`),
  deleteStaking: (stakingId) => api.delete(`/admin/stakings/${stakingId}`),
  
  // Задания
  getTasks: () => api.get('/admin/tasks'),
  createTask: (task) => api.post('/admin/tasks', task),
  updateTask: (taskId, data) => api.put(`/admin/tasks/${taskId}`, data),
  deleteTask: (taskId) => api.delete(`/admin/tasks/${taskId}`),
  getTaskTypes: () => api.get('/admin/task-types'),
  getTasksStats: () => api.get('/admin/tasks-stats'),

  // Аналитика заданий
  getTasksAnalytics: () => api.get('/admin/tasks-analytics'),
  getTasksLeaderboard: (params) => api.get('/admin/tasks-leaderboard', { params }),
  getTasksFeed: (params) => api.get('/admin/tasks-feed', { params }),
  getTasksUserDetails: (userId) => api.get(`/admin/tasks-user/${userId}`),

  // Ads метрики
  getAdsStats: () => api.get('/admin/ads-stats'),
  getAdsLeaderboard: (params) => api.get('/admin/ads-leaderboard', { params }),
  getAdsUserTransactions: (userId, params) => api.get(`/admin/ads-user/${userId}`, { params }),
  getAdsgramStats: () => api.get('/admin/adsgram-stats'),

  // Партнёрские каналы
  getPartnerChannels: () => api.get('/admin/partner-channels'),
  createPartnerChannel: (data) => api.post('/admin/partner-channels', data),
  updatePartnerChannel: (id, data) => api.put(`/admin/partner-channels/${id}`, data),
  deletePartnerChannel: (id) => api.delete(`/admin/partner-channels/${id}`),
  resolveChannel: (input) => api.post('/admin/partner-channels/resolve-channel', { channel_input: input }),
  checkBotInChannel: (channelId) => api.post('/admin/partner-channels/check-bot', { channel_id: channelId }),
  getPartnerAnalytics: () => api.get('/admin/partner-channels/analytics'),
  checkAllChannelBots: () => api.get('/admin/partner-channels/bot-status'),
  
  // Игры
  getGames: (params) => api.get('/admin/minigames', { params }),

  // Настройки ротации
  getRotationSettings: () => api.get('/admin/minigames/rotation-settings'),
  updateRotationSettings: (settings) => api.put('/admin/minigames/rotation-settings', settings),
  forceGame: (gameId) => api.post('/admin/minigames/force-game', { gameId }),
  rotateNow: () => api.post('/admin/minigames/rotate-now'),

  // Конфигурация игр
  getMinigamesConfig: () => api.get('/admin/minigames/config'),
  updateMinigameConfig: (gameType, config) => api.put(`/admin/minigames/config/${gameType}`, config),
  toggleMinigame: (gameType, enabled) => api.post(`/admin/minigames/${gameType}/toggle`, { enabled }),
  reloadMinigamesSettings: () => api.post('/admin/minigames/reload-settings'),
  
  // Логи
  getLogs: (params) => api.get('/admin/logs', { params }),

  // Выводы
  getWithdrawals: (params) => api.get('/admin/withdrawals', { params }),
  getWithdrawalsStats: () => api.get('/admin/withdrawals/stats'),
  getWithdrawalById: (id) => api.get(`/admin/withdrawals/${id}`),
  approveWithdrawal: (id) => api.post(`/admin/withdrawals/${id}/approve`),
  rejectWithdrawal: (id, data) => api.post(`/admin/withdrawals/${id}/reject`, data),
  processWithdrawal: (id) => api.post(`/admin/withdrawals/${id}/process`),
  getAutoApproveMode: () => api.get('/admin/auto-approve-withdrawals'),
  setAutoApproveMode: (payload) => api.put('/admin/auto-approve-withdrawals', payload),
  getSystemWalletBalance: () => api.get('/admin/system-wallet-balance'),
  // Блокчейн-обороты юзера (сколько реально завёл/вывел ончейн, раздельно SPERM/USDT)
  getUserBlockchainTurnover: (userId) => api.get(`/admin/withdrawals/user/${userId}/blockchain-turnover`),
  // Лимиты контроля выводов
  getWithdrawalLimits: () => api.get('/admin/withdrawals/limits'),
  setWithdrawalLimits: (limits) => api.post('/admin/withdrawals/limits', limits),

  // Черный список выводов (по ID вывода)
  addToWithdrawalBlacklist: (withdrawalId, data) => api.post(`/admin/withdrawals/${withdrawalId}/blacklist`, data),
  removeFromWithdrawalBlacklist: (withdrawalId) => api.post(`/admin/withdrawals/${withdrawalId}/unblacklist`),
  getWithdrawalBlacklist: (params) => api.get('/admin/withdrawal-blacklist', { params }),

  // Черный список выводов (по ID пользователя)
  addUserToWithdrawalBlacklist: (userId, reason) => api.post(`/admin/withdrawal-blacklist/${userId}`, { reason }),
  removeUserFromWithdrawalBlacklist: (userId) => api.delete(`/admin/withdrawal-blacklist/${userId}`),

  // Whitelist выводов (по ID вывода)
  addToWithdrawalWhitelist: (withdrawalId, data) => api.post(`/admin/withdrawals/${withdrawalId}/whitelist`, data),
  removeFromWithdrawalWhitelist: (withdrawalId) => api.post(`/admin/withdrawals/${withdrawalId}/unwhitelist`),

  // Whitelist выводов (по ID пользователя)
  addUserToWithdrawalWhitelist: (userId, reason) => api.post(`/admin/withdrawal-whitelist/${userId}`, { reason }),
  removeUserFromWithdrawalWhitelist: (userId) => api.delete(`/admin/withdrawal-whitelist/${userId}`),

  // Приостановка реферальной программы
  toggleReferralSuspension: (userId, reason) => api.post(`/admin/users/${userId}/toggle-referral-suspension`, { reason }),
  getReferralSuspensionStatus: (userId) => api.get(`/admin/users/${userId}/referral-suspension-status`),

  // Burn Pool
  getBurnPoolStatus: () => api.get('/admin/burn-pool/status'),
  executeBurn: (data) => api.post('/admin/burn-pool/execute', data),
  executeManualBurn: (amount, sendBroadcast = false, usePoolAmount = false) => api.post('/admin/burn-pool/execute-manual', { amount, sendBroadcast, usePoolAmount }),
  getWalletBalance: () => api.get('/admin/burn-pool/wallet-balance'),
  getBurnPoolHistory: (params) => api.get('/admin/burn-pool/history', { params }),
  getBurnPoolStats: () => api.get('/admin/burn-pool/stats'),
  getBroadcastStatus: (broadcastId) => api.get(`/admin/broadcasts/${broadcastId}`),

  // Активность в реальном времени
  get: (url, config) => api.get(`/admin${url}`, config),
  post: (url, data, config) => api.post(`/admin${url}`, data, config),
  // PUT не было вовсе: одиночные ручки вида /jarvis/settings/:key звать
  // было нечем. Добавлен 09.09.2026 под тумблеры, применяемые сразу.
  put: (url, data, config) => api.put(`/admin${url}`, data, config),
  delete: (url, config) => api.delete(`/admin${url}`, config),
  getActivity: (params) => api.get('/admin/activity/realtime', { params }),
  getActivityStats: (params) => api.get('/admin/activity/stats', { params }),

  // Broadcasts (Push-уведомления)
  getBroadcasts: (params) => api.get('/admin/broadcasts', { params }),
  getBroadcastStats: () => api.get('/admin/broadcasts/stats'),
  getBroadcastQueueStatus: () => api.get('/admin/broadcasts/queue'),
  createBroadcast: (data) => api.post('/admin/broadcasts', data),
  deleteBroadcast: (id) => api.delete(`/admin/broadcasts/${id}`),
  clearBroadcastHistory: () => api.delete('/admin/broadcasts/history'),
  pauseBroadcastQueue: () => api.post('/admin/broadcasts/queue/pause'),
  resumeBroadcastQueue: () => api.post('/admin/broadcasts/queue/resume'),
  stopBroadcastQueue: () => api.post('/admin/broadcasts/queue/stop'),
  // Загрузка медиа для рассылок
  uploadBroadcastMedia: (file) => {
    const formData = new FormData();
    formData.append('media', file);
    return api.post('/admin/broadcasts/upload-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Поиск пользователей для рассылки по структуре
  searchUsersForBroadcast: (query) => api.get('/admin/broadcasts/search-users', { params: { query } }),
  // Превью структуры пользователя (с фильтрами)
  getStructurePreview: (userId, depth, hasStaking = false, noReferrals = false) =>
    api.get('/admin/broadcasts/structure-preview', { params: { userId, depth, hasStaking, noReferrals } }),

  // Market Maker
  getMarketMakerStatus: () => api.get('/admin/market-maker/status'),
  getMarketMakerSettings: () => api.get('/admin/market-maker/settings'),
  saveMarketMakerSettings: (settings) => api.post('/admin/market-maker/settings', settings),
  toggleMarketMaker: (data) => api.post('/admin/market-maker/toggle', data),
  getMarketMakerWallets: (params) => api.get('/admin/market-maker/wallets', { params }),
  addMarketMakerWallet: (data) => api.post('/admin/market-maker/wallets', data),
  updateMarketMakerWallet: (id, data) => api.put(`/admin/market-maker/wallets/${id}`, data),
  deleteMarketMakerWallet: (id) => api.delete(`/admin/market-maker/wallets/${id}`),
  getMarketMakerStats: (params) => api.get('/admin/market-maker/stats', { params }),
  getMarketMakerTrades: (params) => api.get('/admin/market-maker/trades', { params }),
  getMarketMakerEvents: (params) => api.get('/admin/market-maker/events', { params }),
  getMarketMakerLogs: (params) => api.get('/admin/market-maker/logs', { params }),
  resetMarketMakerCircuitBreaker: () => api.post('/admin/market-maker/circuit-breaker/reset'),
  getMarketMakerPrice: () => api.get('/admin/market-maker/price'),

  // Market Maker - Scanner
  scanProfitableWallets: (data) => api.post('/admin/market-maker/scan-wallets', data),
  getWalletDetails: (address, days = 30) => api.get(`/admin/market-maker/wallet-details/${encodeURIComponent(address)}?days=${days}`),
  addFlipper: (data) => api.post('/admin/market-maker/add-flipper', data),

  // Market Maker - Flipper Scanner (автоматический)
  getFlipperCandidates: (params) => api.get('/admin/market-maker/flipper-candidates', { params }),
  approveFlipperCandidate: (id) => api.post(`/admin/market-maker/flipper-candidates/${id}/approve`),
  rejectFlipperCandidate: (id, notes) => api.post(`/admin/market-maker/flipper-candidates/${id}/reject`, { notes }),
  deleteFlipperCandidate: (id) => api.delete(`/admin/market-maker/flipper-candidates/${id}`),
  runFlipperScan: () => api.post('/admin/market-maker/flipper-candidates/scan'),
  getFlipperScannerStats: () => api.get('/admin/market-maker/flipper-scanner/stats'),

  // Distribution System
  getDistributionStatus: () => api.get('/admin/distribution/status'),
  startDistribution: () => api.post('/admin/distribution/start'),
  stopDistribution: () => api.post('/admin/distribution/stop'),
  triggerDistributionActivity: () => api.post('/admin/distribution/activity'),
  getDistributionWallets: (params) => api.get('/admin/distribution/wallets', { params }),
  getDistributionLogs: (params) => api.get('/admin/distribution/logs', { params }),
  exportDistributionSeeds: (password) => api.post('/admin/distribution/export', { password }),
  getDistributionWalletBalance: (walletId) => api.get(`/admin/distribution/wallets/${walletId}/balance`),
  distributionSend: (data) => api.post('/admin/distribution/send', data, { timeout: 120000 }),
  distributionSweep: (data) => api.post('/admin/distribution/sweep', data, { timeout: 10000 }),
  getSweepStatus: (sweepId) => api.get(`/admin/distribution/sweep/${sweepId}/status`),
  abortSweep: (sweepId) => api.post(`/admin/distribution/sweep/${sweepId}/abort`),
  refreshDistributionBalances: (data) => api.post('/admin/distribution/refresh-balances', data, { timeout: 180000 }),
  getDistributionDiagnostic: () => api.get('/admin/distribution/diagnostic'),

  // Режим роста курса
  getGrowthStatus: () => api.get('/admin/market-maker/growth/status'),
  toggleGrowth: (data) => api.post('/admin/market-maker/growth/toggle', data),
  updateGrowthRate: (data) => api.post('/admin/market-maker/growth/update-rate', data),

  // Wallet Scanner - Deep Connections
  startDeepConnectionScan: (address, depth = 3, maxPerLevel = 20) =>
    api.post('/admin/wallet-scanner/deep-connections', { address, depth, maxPerLevel }),
  getDeepConnectionStatus: (taskId) =>
    api.get(`/admin/wallet-scanner/deep-connections/${taskId}`),
  abortDeepConnectionScan: (taskId) =>
    api.post(`/admin/wallet-scanner/deep-connections/${taskId}/abort`),

  // 🎟️ Промокоды Lunar Start
  getPromoCodes: () => api.get('/admin/promo/list'),
  createPromoCode: (data) => api.post('/admin/promo/create', data),
  togglePromoCode: (id, isActive) => api.post('/admin/promo/toggle', { id, isActive }),
  getPromoActivations: () => api.get('/admin/promo/activations'),

  // 🎟️ Промокоды казино (вкладка «Слоты/Казино»)
  getCasinoPromoCodes: () => api.get('/admin/casino-promo/list'),
  createCasinoPromoCode: (data) => api.post('/admin/casino-promo/create', data),
  toggleCasinoPromoCode: (id, isActive) => api.post('/admin/casino-promo/toggle', { id, isActive }),
  deleteCasinoPromoCode: (id) => api.post('/admin/casino-promo/delete', { id }),
  getCasinoPromoActivations: (promoId) => api.get('/admin/casino-promo/activations', promoId ? { params: { promoId } } : undefined),

  // 🎰 Лояльность слотов (MoonStash + MoonRush)
  getLoyaltyOverview: () => api.get('/admin/loyalty/overview'),
  toggleLoyalty: (enabled) => api.post('/admin/loyalty/toggle', { enabled }),
  getLoyaltyLeaderboard: () => api.get('/admin/loyalty/leaderboard'),
  getLoyaltyWagering: (page = 1, perPage = 25) =>
    api.get(`/admin/loyalty/wagering?page=${page}&perPage=${perPage}`),
  getLoyaltyPayoutsRecent: (limit = 30) => api.get(`/admin/loyalty/payouts/recent?limit=${limit}`),
  getLoyaltyPayoutsArchive: (page = 1, perPage = 36) =>
    api.get(`/admin/loyalty/payouts/archive?page=${page}&perPage=${perPage}`),
  grantLoyaltyTest: (userId, amount, source = 'admin_adjust') =>
    api.post('/admin/loyalty/test-grant', { userId, amount, source }),
  grantLoyaltyWheelSpins: (userId, count = 7) =>
    api.post('/admin/loyalty/test-wheel-spins', { userId, count }),
  getLoyaltySettings: () => api.get('/admin/loyalty/settings'),
  getLoyaltyFeed: (limit = 100, offset = 0, filters = {}) => {
    const params = new URLSearchParams({ limit, offset });
    if (filters.type) params.append('type', filters.type);
    if (filters.source) params.append('source', filters.source);
    if (filters.wager_status) params.append('wager_status', filters.wager_status);
    return api.get(`/admin/loyalty/feed?${params.toString()}`);
  },
  getLoyaltyLeaders: (topN = 10) => api.get(`/admin/loyalty/leaders?topN=${topN}`),

  // 🤝 Реферальная система слотов (только чтение)
  getSlotReferralsStats: () => api.get('/admin/loyalty/referrals/stats'),
  getSlotReferralsTop: (limit = 50) => api.get(`/admin/loyalty/referrals/top?limit=${limit}`),
  getSlotReferralsFeed: (limit = 100) => api.get(`/admin/loyalty/referrals/feed?limit=${limit}`),
  getSlotReferralsPairs: (page = 1, perPage = 50) =>
    api.get(`/admin/loyalty/referrals/pairs?page=${page}&perPage=${perPage}`),

  // CPA Партнёры (Tower TON)
  getPartnerStats: () => api.get('/admin/partners/stats'),
  getPartnerUsers: (params) => api.get('/admin/partners/users', { params }),
  getPartnerSettings: () => api.get('/admin/partners/settings'),
  savePartnerSettings: (data) => api.post('/admin/partners/settings', data),
  retryPartnerApprove: (id) => api.post(`/admin/partners/retry/${id}`),

  // Трафик-ссылки
  getTrafficLinks: (params) => api.get('/admin/traffic-links', { params }),
  createTrafficLink: (data) => api.post('/admin/traffic-links', data),
  getTrafficLinkDetails: (id, params) => api.get(`/admin/traffic-links/${id}`, { params }),
  deleteTrafficLink: (id) => api.delete(`/admin/traffic-links/${id}`),
};

export default api;